import { Observable, Subject, map, filter, BehaviorSubject } from 'rxjs';

import { ajaxGet, ajaxGetType, ajaxPost } from 'common/ajax';
import { getAvaDataManager, getODataV4DataManager } from 'common/AVADataManager';
import { Stat } from 'components/CRM/AgentProductivity/AgentProductivityStats';
import { MealsValidationDataResponse } from 'components/CRM/Common/hooks/useSsbciMealsValidation';
import { HealthPlanBenefitDetailsParameters, HealthPlanParameters } from 'interfaces/ADAS/HealthPlanService';
import { Case } from 'interfaces/CRM/Case/Case';
import CampaignRequest from 'interfaces/CRM/Requests/CampaignRequest';
import { AddActionTakenToCaseRequest, ReassignCaseRequest } from 'interfaces/CRM/Requests/CaseRequest';
import CreateCaseRequest from 'interfaces/CRM/Requests/CreateCaseRequest';
import { CreateInteractionRequest } from 'interfaces/CRM/Requests/CreateInteractionRequest';
import CreateOutboundSurveyRequest from 'interfaces/CRM/Requests/CreateOutboundSurveyRequest';
import { CreateWorkflowRequest } from 'interfaces/CRM/Requests/CreateWorkflowRequest';
import CrmCommunicationPreferenceUpdateRequest from 'interfaces/CRM/Requests/CrmCommunicationPreferenceUpdateRequest';
import { UpdateTextReplaceOptions } from 'interfaces/CRM/Requests/CrmConfigRequest';
import EscalateWorkflowRequest from 'interfaces/CRM/Requests/EscalateWorkflowRequest';
import { CreateFormTemplateRequest, UpdateFormTemplateRequest } from 'interfaces/CRM/Requests/FormTemplateRequest';
import GetMemberCallLogsRequest from 'interfaces/CRM/Requests/GetMemberCallLogsRequest';
import MarkNbaCompleteRequest from 'interfaces/CRM/Requests/MarkNbaCompleteRequest';
import ProcessDemographicChangeRequest from 'interfaces/CRM/Requests/ProcessDemographicChangeRequest';
import ProcessPcpChangeRequest from 'interfaces/CRM/Requests/ProcessPcpChangeRequest';
import ProcessPoaValidationRequest from 'interfaces/CRM/Requests/ProcessPoaValidationRequest';
import { ProcessSSBCIRequest } from 'interfaces/CRM/Requests/ProcessSSBCIRequest';
import { CreateServiceFlowRequest, UpdateServiceFlowRequest } from 'interfaces/CRM/Requests/ServiceFlowRequests';
import StickyNoteRequest from 'interfaces/CRM/Requests/StickyNoteRequest';
import SubmitMemberReimbursementRequest from 'interfaces/CRM/Requests/SubmitMemberReimbursementRequest';
import SwitchIsCaseClosedRequest from 'interfaces/CRM/Requests/SwitchIsCaseClosedRequest';
import { UpdateCrmBenefitInfoRequest } from 'interfaces/CRM/Requests/UpdateCrmBenefitInfoRequest';
import { UpdateInteractionRequest } from 'interfaces/CRM/Requests/UpdateInteractionRequest';
import { UpdateWorkflowRequest } from 'interfaces/CRM/Requests/UpdateWorkflowRequest';
import UpsertSupplementalBenefitConfigRequest from 'interfaces/CRM/Requests/UpsertSupplementalBenefitConfigRequest';
import ServiceFlowConfigRequest from 'interfaces/CRM/Requests/WorkflowOptionsRequest';
import { UpdateCaseReassignmentStructureByQueueRequest } from 'interfaces/CRM/Requests/WorkflowTaskOptionsRequest';
import { CreateWorkflowTaskRequest, UpdateWorkflowTaskRequest } from 'interfaces/CRM/Requests/WorkflowTaskRequest';
import { BenefitValidationError } from 'interfaces/CRM/Responses/BenefitValidationError';
import { GetMemberWalletsResponse } from 'interfaces/CRM/Responses/GetMemberWalletsResponse';
import { OnePassCode } from 'interfaces/CRM/Responses/OnePassCode';
import { ServiceFlow } from 'interfaces/CRM/ServiceFlow';
import { ProcessRequestPlanMaterialForm } from 'interfaces/CRM/Workflows/WorkflowForms/RequestPlanMaterialForm';
import { WorkflowTask } from 'interfaces/CRM/Workflows/WorkflowTask';
import ProviderSearchParameters from 'interfaces/ProviderSearch/Requests/ProviderSearchParameters';

import Auth from '../../Auth';
import { MailRequestItems } from '../../interfaces/CRM/MailRequestItems';
import ComplaintTagInfo from '../../interfaces/CRM/Tags/ComplaintTagInfo';
import MemberTag from '../../interfaces/MemberTag';
import { getODataArray, getODataObject, ODataArrayResponse } from './ODataAjaxUtil';
/*
 * @deprecated use ODataAjaxUtil
 */
const ajaxGetResponse = <TResponse = any>(url) =>
    ajaxGet<TResponse>(url).pipe(map((ajaxResponse) => ajaxResponse.response));

const productivityStatExecuted = new Subject<Stat>();
const tabsSubject = new Subject();
const memberCasesSubject = new BehaviorSubject(null);
const defaultCrmMemberTimeSubject: any = {};
const crmMemberTimelineSubject = new BehaviorSubject(defaultCrmMemberTimeSubject);

const myQueueService = {
    exportExcelUrls: {
        tasks: `/odata/crm/myqueue/default.csvexportworkflowtasks`,
    },
    subjects: {
        refreshOutbound: new Subject(),
        refreshCases: new Subject(),
        refreshInteractions: new Subject(),
        refreshTasks: new Subject(),
        refreshWorkflows: new Subject(),
    },
    dataManagers: {
        prescribers: () => getODataV4DataManager('odata/crm/myqueue/default.getprescribers'),
        pharmacies: () => getODataV4DataManager('odata/crm/myqueue/default.getpharmacies'),
        campaigns: () => getODataV4DataManager('odata/crm/myqueue/default.getcampaigns'),
        campaignEligibleMembers: () => getODataV4DataManager(`/odata/crm/myqueue/default.getcampaigneligiblemembers`),
        caseOwners: () => getODataV4DataManager(`/odata/crm/myqueue/default.GetCaseOwners?$OrderBy=Text`),
        caseModifiedBy: () => getODataV4DataManager(`/odata/crm/myqueue/default.GetCaseModifiedBy`),
        cases: () => getODataV4DataManager('odata/crm/myqueue/default.getcases'),
        caseStatuses: () => getODataV4DataManager(`/odata/crm/myqueue/default.getcasestatuses`),
        formTemplates: () => getODataV4DataManager('odata/crm/myqueue/default.getformtemplates'),
        interactions: () => getODataV4DataManager('odata/crm/myqueue/default.getinteractions'),
        interactionCallDirections: () =>
            getODataV4DataManager('odata/crm/myqueue/default.getInteractionCallDirections'),
        interactionCallOutcomes: () => getODataV4DataManager('odata/crm/myqueue/default.getInteractionCallOutcomes'),
        interactionCreatedBy: () => getODataV4DataManager('odata/crm/myqueue/default.getInteractionCreatedBy'),
        interactionTypes: () => getODataV4DataManager('odata/crm/myqueue/default.getInteractionTypes'),
        languages: () => getODataV4DataManager(`/odata/crm/myqueue/default.getlanguages`),
        ezMemberLanguages: () => getODataV4DataManager(`/odata/crm/myqueue/default.getezmemberlanguages`),
        getWorklistStats$: (userEmail: string) =>
            getODataObject(`/odata/crm/myqueue/default.GetWorklistStats(userEmail='${userEmail}')`),
        getWorklistUrgentCases$: (userEmail: string) =>
            getODataV4DataManager(`/odata/crm/myqueue/default.GetWorklistUrgentCases(userEmail='${userEmail}')`),
        getWorklistRecentlyClosedTasks$: (userEmail: string) =>
            getODataV4DataManager(
                `/odata/crm/myqueue/default.GetWorklistRecentlyClosedTasks(userEmail='${userEmail}')`,
            ),
        getWorklistRecentlyClosedRejectedTasks$: (userEmail: string) =>
            getODataV4DataManager(
                `/odata/crm/myqueue/default.GetWorklistRecentlyClosedRejectedTasks(userEmail='${userEmail}')`,
            ),
        getWorklistCasesCandidatesToClose$: (userEmail: string) =>
            getODataV4DataManager(
                `/odata/crm/myqueue/default.GetWorklistCasesCandidatesToClose(userEmail='${userEmail}')`,
            ),
        getWorklistWorkflowsExceedingSlaNoOpenTasks$: (userEmail: string) =>
            getODataV4DataManager(
                `/odata/crm/myqueue/default.GetWorklistWorkflowsExceedingSlaNoOpenTasks(userEmail='${userEmail}')`,
            ),
        getWorklistCasesLastUpdatedByDifferentId$: (userEmail: string) =>
            getODataV4DataManager(
                `/odata/crm/myqueue/default.GetWorklistCasesLastUpdatedByDifferentId(userEmail='${userEmail}')`,
            ),
        getWorklistOpenTasksExceedingSla$: (userEmail: string) =>
            getODataV4DataManager(
                `/odata/crm/myqueue/default.GetWorklistOpenTasksExceedingSla(userEmail='${userEmail}')`,
            ),
        getWorklistWorkflowsExceedingSlaHasOpenTasks$: (userEmail: string) =>
            getODataV4DataManager(
                `/odata/crm/myqueue/default.GetWorklistWorkflowsExceedingSlaHasOpenTasks(userEmail='${userEmail}')`,
            ),
        getWorklistCasesWithinSla$: (userEmail: string) =>
            getODataV4DataManager(`/odata/crm/myqueue/default.GetWorklistCasesWithinSla(userEmail='${userEmail}')`),
        getWorklistWorkflowsWithinSla$: (userEmail: string) =>
            getODataV4DataManager(`/odata/crm/myqueue/default.GetWorklistWorkflowsWithinSla(userEmail='${userEmail}')`),
        medicalGroups: () => getODataV4DataManager(`/odata/sessionorganizations?OrgType=IPA`),
        memberMedication: (memberId: string) =>
            getAvaDataManager(`/odata/crm/myqueue/default.getMedications?memberid=${memberId}`),
        memberPortalMessageCategories: () =>
            getODataV4DataManager(`odata/crm/myqueue/default.getMemberPortalMessageCategories`),
        outboundSurveys: () => getODataV4DataManager(`/odata/crm/myqueue/default.getOutboundSurveys`),
        outboundSurveyCreatedBy: () => getODataV4DataManager('odata/crm/myqueue/default.getOutboundSurveyCreatedBy'),
        queues: () => getODataV4DataManager('odata/crm/myqueue/default.getqueues'),
        serviceFlows: () => getODataV4DataManager('odata/crm/myqueue/default.getserviceflows'),
        workflows: () => getODataV4DataManager('odata/crm/myqueue/default.getworkflows'),
        workflowCreatedBy: () => getODataV4DataManager('odata/crm/myqueue/default.getworkflowcreatedby'),
        workflowModifiedBy: () => getODataV4DataManager('odata/crm/myqueue/default.getworkflowmodifiedby'),
        workflowClosedBy: () => getODataV4DataManager('odata/crm/myqueue/default.getworkflowclosedby'),
        workflowStatuses: () => getODataV4DataManager('odata/crm/myqueue/default.getWorkflowStatuses'),
        workflowReasons: () => getODataV4DataManager('odata/crm/myqueue/default.getWorkflowReasons'),
        workflowTypes: () => getODataV4DataManager('odata/crm/myqueue/default.getworkflowtypes'),
        workflowTasks: () => getODataV4DataManager('odata/crm/myqueue/default.getworkflowtasks'),
        workflowTaskClosedBy: () => getODataV4DataManager('odata/crm/myqueue/default.getTaskClosedBy'),
        workflowTaskCreatedBy: () => getODataV4DataManager('odata/crm/myqueue/default.getTaskCreatedBy'),
        workflowTaskAssignedTo: () => getODataV4DataManager('odata/crm/myqueue/default.getWorkflowTaskAssignedTo'),
        workflowTaskStatuses: () => getODataV4DataManager('odata/crm/myqueue/default.getWorkflowTaskStatuses'),
        workflowTaskTypes: () => getODataV4DataManager('odata/crm/myqueue/default.getTaskTypes'),
        activeCounties: () => getODataV4DataManager('odata/crm/myqueue/default.getActiveCountiesOptions'),
        memberReimbursementStatuses: () =>
            getODataV4DataManager('odata/crm/myqueue/default.getMemberReimbursementStatuses'),
    },
    urls: {
        GetActiveServiceFlowTaskTypes: '/odata/crm/myqueue/default.GetActiveServiceFlowTaskTypes',
        GetFormTemplates: '/odata/crm/myqueue/default.getFormTemplates',
        GetDomains: '/odata/domains',
        RenameDomain: (domainId: number) => `/api/domains/${domainId}/rename`,
        DomainOrganizations: (domainId: number) => `/api/domains/${domainId}/organizations`,
        DomainAdmins: (domainId: number) => `/api/domains/${domainId}/admins`,
    },
    getIPAAdditionalContacts$: (ipaCode: string) =>
        ajaxGetResponse(`/odata/crm/myqueue/default.GetIPAAdditionalContacts(ipacode='${ipaCode}')`),
    // campaign
    createCampaign$: (request: CampaignRequest) => ajaxPost('/odata/crm/myqueue/default.createCampaign', request),
    updateCampaign$: (request: CampaignRequest) => ajaxPost('/odata/crm/myqueue/default.updateCampaign', request),
    deleteCampaign$: (id: string) =>
        ajaxPost('/odata/crm/myqueue/default.deleteCampaign', {
            id: id,
        }),
    switchIsActiveCampaign$: (id: string) => ajaxPost('/odata/crm/myqueue/default.SwitchIsActiveCampaign', { id: id }),

    getCampaigns$: () => ajaxGetResponse('odata/crm/myqueue/default.getcampaigns'),
    getCampaignById$: (campaignId: string) =>
        ajaxGetResponse(`odata/crm/myqueue/default.getcampaigns?$filter=(id eq '${campaignId}')`).pipe(
            map((response) => response.value),
            filter((results) => results && results.length),
            map((results) => results[0]),
        ),

    getMemberCountOfCampaignById$: (campaignId: string) =>
        ajaxGetResponse(`odata/crm/myqueue/default.GetMemberCountOfCampaignById?id=${campaignId}`),

    // form template
    createFormTemplate$: (body: CreateFormTemplateRequest) =>
        ajaxPost(`/odata/crm/myqueue/default.createFormTemplate`, body).pipe(
            map((ajaxResponse) => ajaxResponse.response),
        ),
    updateFormTemplate$: (body: UpdateFormTemplateRequest) =>
        ajaxPost(`/odata/crm/myqueue/default.updateFormTemplate`, body).pipe(
            map((ajaxResponse) => ajaxResponse.response),
        ),
    deleteFormTemplate$: (id: string) =>
        ajaxPost(`/odata/crm/myqueue/default.deleteFormTemplate`, { id: id }).pipe(
            map((ajaxResponse) => ajaxResponse.response),
        ),
    duplicateFormTemplate$: (id: string) =>
        ajaxPost(`/odata/crm/myqueue/default.duplicateFormTemplate`, { id: id }).pipe(
            map((ajaxResponse) => ajaxResponse.response),
        ),

    getFormTemplates$: () => ajaxGetResponse(`/odata/crm/myqueue/default.getFormTemplates`),
    getFormTemplateById$: (formTemplateId: string) =>
        ajaxGetResponse(`/odata/crm/myqueue/default.getformtemplates?$filter=(id eq '${formTemplateId}')`).pipe(
            map((response) => response.value),
            filter((results) => results && results.length),
            map((results) => results[0]),
        ),

    getFormTemplateFieldTypes$: () => ajaxGetResponse(`/odata/crm/myqueue/default.getFormTemplateFieldTypes`),

    // member
    searchMember$: (searchTerm: string) =>
        ajaxGetResponse(`/odata/crm/myqueue/default.searchmembers?searchterm=${searchTerm}`),

    // OData member
    searchODataMember$: (searchTerm: string) =>
        getODataV4DataManager(`/odata/crm/myqueue/default.searchmembers?searchterm=${searchTerm}`),
    // returns active member profile
    memberProfile$: (memberId: string) =>
        ajaxGetResponse(`/odata/crm/myqueue/default.getmember(memberId='${memberId}')`),
    // returns list for member for all statuses (termed, active, future confirmed)
    getMemberAllStatuses$: (memberId: string) =>
        ajaxGet(`/odata/crm/myqueue/default.getmemberallstatuses(memberId='${memberId}')`),
    memberTagDetails$: (memberId: string) =>
        ajaxGetResponse(`/odata/crm/myqueue/default.getmembertagdetails(memberId='${memberId}')`),
    getJsaAppointmentTagDetails$: (memberId: string) =>
        ajaxGetResponse(`/odata/crm/myqueue/default.getJsaAppointmentTagDetails(memberId='${memberId}')`),
    getAppealGrievanceCtmTagDetails$: (memberId: string): Observable<ComplaintTagInfo[]> =>
        getODataArray(`/odata/crm/myqueue/default.getAppealGrievanceCtmTagDetails(memberId='${memberId}')`),
    getDmeProcedureCategories$: (memberId: string) =>
        ajaxGetResponse(`/odata/crm/myqueue/default.getDmeProcedureCategories(memberId='${memberId}')`),
    getClinicalAlertRegistries$: (memberId: string) =>
        ajaxGetResponse(`/odata/crm/myqueue/default.getClinicalAlertRegistries(memberId='${memberId}')`),
    getCtm$: (memberId: string) => ajaxGetResponse(`/odata/crm/myqueue/default.getCtm(memberId='${memberId}')`),
    memberNextBestActions$: (memberId: string) =>
        ajaxGetResponse(`/odata/crm/myqueue/default.GetNextBestActions(memberId='${memberId}')`).pipe(
            map((odataResponse) => odataResponse.value),
        ),

    memberSupplementalBenefits$: (memberId: string) =>
        ajaxGetResponse(`/odata/supplementalbenefits?$filter=memberId eq '${memberId}'`),

    searchMembersByPhoneNumber$: (phone: string) =>
        ajaxGetResponse(`/odata/crm/myqueue/default.SearchMembersByPhoneNumber(phoneNumber='${phone}')`),

    searchMembersForInbound$: (name: string, phoneNumber: string, memberIds: string[], birthDate: Date) =>
        ajaxGetResponse(
            `/odata/crm/myqueue/default.SearchMembersForInbound(` +
                `name=${getStringProperty(name)}, ` +
                `phoneNumber=${getStringProperty(phoneNumber, true)}, ` +
                `memberIds=${getArrayToStringProperty(memberIds)}, ` +
                `birthDate=${getDateProperty(birthDate)})`,
        ),

    getMemberCommunicationPreferences$: (memberId: string, companyCode: string) =>
        ajaxGet(
            `/odata/crm/myqueue/default.getmembercommunicationpreferences(memberId='${memberId}',companyCode='${companyCode}')`,
        ).pipe(map((ajaxResponse) => ajaxResponse.response)),

    updateMemberCommunicationPreferences$: (body: CrmCommunicationPreferenceUpdateRequest) =>
        ajaxPost(`/odata/crm/myqueue/default.updatemembercommunicationpreferences`, body).pipe(
            map((ajaxResponse) => ajaxResponse.response),
        ),

    getMemberClaims$: (memberId: string) =>
        ajaxGetResponse(`/odata/crm/myqueue/default.GetMemberClaims(memberId='${memberId}')`),
    getMemberClaimByNo$: (memberId: string, claimNo: string) =>
        getODataObject(`/odata/crm/myqueue/default.GetMemberClaimByNo(memberId='${memberId}',claimNo='${claimNo}')`),

    getMyOpenReminders$: () => ajaxGetResponse('/odata/crm/myqueue/default.GetMyOpenReminders'),
    getMemberCard$: (memberId: string, companyId: string) =>
        ajaxGetResponse(`/odata/crm/myqueue/default.GetMemberCard(memberId='${memberId}',companyId='${companyId}')`),

    getMemberEligibilityVerification$: (
        memberId: string,
        healthPlanId: string,
        pbpId: string,
        planYear: string,
        userTimezoneOffset: number,
    ) =>
        ajaxGetType(
            `/odata/crm/myqueue/default.GetMemberEligibilityVerificationDocument?memberId=${memberId}&Hcode=${healthPlanId}&pbpid=${pbpId}&planyear=${planYear}&userTimezoneOffset=${userTimezoneOffset}`,
            'blob',
        ),

    getPoaMembers$: (memberId: string) =>
        ajaxGetResponse(`/odata/crm/myqueue/default.GetPoaMembers(memberId='${memberId}')`),

    getMembershipDocByFilePath$: (filePath: string) =>
        ajaxGetResponse(`/odata/crm/myqueue/default.GetMembershipDocByFilePath?filePath=${filePath}`),

    // interaction
    createInteraction$: (body: CreateInteractionRequest) =>
        ajaxPost('/odata/crm/myqueue/default.createinteraction', body).pipe(
            map((ajaxResponse) => ajaxResponse.response),
        ),
    updateInteraction$: (body: UpdateInteractionRequest) =>
        ajaxPost(`/odata/crm/myqueue/default.UpdateInteraction`, body).pipe(
            map((ajaxResponse) => ajaxResponse.response),
        ),

    getInteractionsByMemberId$: (memberId: string) =>
        ajaxGetResponse(`/odata/crm/myqueue/default.getinteractions?$filter=memberId eq '${memberId}'&$skip=0&$top=50`),
    getInteractionsByCaseId$: (caseId: string) =>
        ajaxGetResponse(
            `/odata/crm/myqueue/default.getinteractions?$filter=CaseIds/any(x: x eq '${caseId}')&$orderby=CreatedDateTime desc&$skip=0&$top=50`,
        ),

    markMemberPortalMessagesRead$: (interactionId: string, memberId: string) =>
        ajaxPost(`/odata/crm/myqueue/default.markMemberPortalMessagesRead`, { interactionId, memberId }).pipe(
            map((ajaxResponse) => ajaxResponse.response),
        ),
    getFileContentForInteraction$: (fileId: string) =>
        ajaxGet(`/odata/crm/myqueue/default.GetFileContentForInteraction(fileId=${fileId})`).pipe(
            map((ajaxResponse) => ajaxResponse.response),
        ),
    getInteractionCallDirections$: () => ajaxGetResponse(`/odata/crm/myqueue/default.getInteractionCallDirections`),
    getInteractionCallOutcomes$: () => ajaxGetResponse(`/odata/crm/myqueue/default.getInteractionCallOutcomes`),
    getInteractionTypes$: () => ajaxGetResponse(`/odata/crm/myqueue/default.getInteractionTypes`),
    upsertCallNotes$: (talkdeskId: string, userId: string, notes: string) =>
        ajaxPost(`/odata/crm/myqueue/default.upsertCallNotes`, { talkdeskId, notes, agentName: userId }).pipe(
            map((ajaxResponse) => ajaxResponse.response),
        ),

    // outbound survey
    getOutboundSurveysByCaseId$: (caseId: string) =>
        ajaxGetResponse(
            `/odata/crm/myqueue/default.getoutboundsurveys?$filter=CaseId eq '${caseId}'&$orderby=CreatedDateTime desc&$skip=0&$top=50`,
        ),
    createOutboundSurvey$: (body: CreateOutboundSurveyRequest) =>
        ajaxPost(`/odata/crm/myqueue/default.CreateOutboundSurvey`, body),

    // case
    createCase$: (body: CreateCaseRequest) =>
        ajaxPost(`/odata/crm/myqueue/default.CreateCase`, body).pipe(map((ajaxResponse) => ajaxResponse.response)),
    switchIsCaseClosed$: (request: SwitchIsCaseClosedRequest) =>
        ajaxPost(`/odata/crm/myqueue/default.SwitchIsCaseClosed`, request).pipe(
            map((ajaxResponse) => ajaxResponse.response),
        ),
    reassignCase$: (body: ReassignCaseRequest) =>
        ajaxPost(`/odata/crm/myqueue/default.ReassignCase`, body).pipe(map((ajaxResponse) => ajaxResponse.response)),
    addNoteToCase$: (caseId: string, notes: string) =>
        ajaxPost(`/odata/crm/myqueue/default.AddNoteToCase`, { caseId: caseId, notes: notes }).pipe(
            map((ajaxResponse) => ajaxResponse.response),
        ),
    addActionTakenToCase$: (body: AddActionTakenToCaseRequest) =>
        ajaxPost(`/odata/crm/myqueue/default.AddActionTakenToCase`, body).pipe(
            map((ajaxResponse) => ajaxResponse.response),
        ),

    getAllMemberCases$: (memberId: string) =>
        ajaxGetResponse(`/odata/crm/myqueue/default.getallmembercases(memberId='${memberId}')`),

    getFailedFirstCallResolutions$: (body) =>
        ajaxPost(`/odata/crm/myqueue/default.GetFailedFirstCallResolutions`, body),
    excelExportFailedFirstCallResolutions$: (body) =>
        ajaxPost(`/odata/crm/myqueue/default.ExcelExportFailedFirstCallResolutions`, body, 'blob'),

    getCaseByCaseId$: (caseId: string) =>
        ajaxGetResponse(`/odata/crm/myqueue/default.getCases?$filter=id eq '${caseId}'&$skip=0&$top=50`).pipe(
            map((response) => response.value),
            filter((results) => results && results.length),
            map((results) => results[0]),
        ),

    getCaseOwners$: () => ajaxGetResponse(`/odata/crm/myqueue/default.getCaseOwners`),

    // this is used for memberverticaltimeline
    updateMemberCases: (value) => memberCasesSubject.next(value),
    memberCases$: memberCasesSubject.asObservable(),
    tabs$: tabsSubject.asObservable(),
    tabsNext: (tab) => tabsSubject.next(tab),

    productivityStat: (stat) => productivityStatExecuted.next(stat),
    onProductivityStatExecute: productivityStatExecuted.asObservable(),

    // workflow
    createWorkflow$: (request: CreateWorkflowRequest) =>
        ajaxPost(`/odata/crm/myqueue/default.CreateWorkflow`, request).pipe(
            map((ajaxResponse) => ajaxResponse.response),
        ),
    updateWorkflow$: (request: UpdateWorkflowRequest) =>
        ajaxPost(`/odata/crm/myqueue/default.UpdateWorkflow`, request).pipe(
            map((ajaxResponse) => ajaxResponse.response),
        ),
    closeWorkflow$: (workflowId: string) =>
        ajaxPost(`/odata/crm/myqueue/default.CloseWorkflow`, {
            workflowId: workflowId,
        }).pipe(map((ajaxResponse) => ajaxResponse.response)),

    addNoteToWorkflow$: (workflowId: string, notes: string) =>
        ajaxPost(`/odata/crm/myqueue/default.AddNoteToWorkflow`, {
            workflowId: workflowId,
            notes: notes,
        }).pipe(map((ajaxResponse) => ajaxResponse.response)),
    getFileContentForWorkflow$: (fileId: string) =>
        ajaxPost(`/odata/crm/myqueue/default.GetFileContentForWorkflow`, {
            fileId: fileId,
        }).pipe(map((ajaxResponse) => ajaxResponse.response)),
    escalateWorkflow$: (body: EscalateWorkflowRequest) =>
        ajaxPost(`/odata/crm/myqueue/default.EscalateWorkflow`, body).pipe(
            map((ajaxResponse) => ajaxResponse.response),
        ),

    getWorkflowsByMemberId$: (memberId: string) =>
        ajaxGetResponse(
            `/odata/crm/myqueue/default.getworkflows?$filter=memberId eq '${memberId}'&$orderby=CreatedDate desc&$skip=0&$top=50`,
        ),
    getWorkflowsByMemberIdAndServiceAction$: (memberId, serviceAction) =>
        ajaxGetResponse(
            `/odata/crm/myqueue/default.getworkflows?$filter=memberId eq '${memberId}' and workflowTypeString eq '${serviceAction}'&$orderby=CreatedDate desc&$skip=0&$top=1000`,
        ),
    getWorkflowsByCaseId$: (caseId: string) =>
        ajaxGetResponse(
            `/odata/crm/myqueue/default.getworkflows?$filter=caseId eq '${caseId}'&$orderby=CreatedDate desc&$skip=0&$top=50`,
        ),
    getWorkflowByWorkflowId$: (workflowId: string) =>
        ajaxGetResponse(`/odata/crm/myqueue/default.getworkflows?$filter=id eq '${workflowId}'&$skip=0&$top=50`).pipe(
            map((response) => response.value),
            filter((results) => results && results.length),
            map((results) => results[0]),
        ),
    trackDiscardedWorkflow$: () => ajaxPost(`odata/crm/myqueue/default.trackdiscardworkflow`),

    getDemographicChangeFieldHistory$: (memberId: string) =>
        ajaxGetResponse(`/odata/crm/myqueue/default.getDemographicChangeFieldHistory(memberId='${memberId}')`),

    demographicChange$: (body: ProcessDemographicChangeRequest) =>
        ajaxPost(`/odata/crm/myqueue/default.DemographicChange`, body),
    pcpChange$: (body: ProcessPcpChangeRequest) => ajaxPost(`/odata/crm/myqueue/default.PcpChange`, body),
    requestPlanMaterial$: (formData: ProcessRequestPlanMaterialForm) =>
        ajaxPost(`/odata/crm/myqueue/default.RequestPlanMaterial`, {
            formData: formData,
        }),
    processSSBCI$: (request: ProcessSSBCIRequest) =>
        ajaxPost('/odata/crm/myqueue/default.ProcessSSBCI', request).pipe(map((ajaxResponse) => ajaxResponse.response)),
    getSsbciMealsValidation$: (memberId: string): Observable<MealsValidationDataResponse> =>
        getODataObject<MealsValidationDataResponse>(
            `/odata/crm/myqueue/default.GetSsbciMealsValidation(memberId='${memberId}')`,
        ),
    poaValidation$: (body: ProcessPoaValidationRequest) => ajaxPost(`/odata/crm/myqueue/default.PoaValidation`, body),

    memberReimbursement$: (body: SubmitMemberReimbursementRequest) =>
        ajaxPost(`/odata/crm/myqueue/default.MemberReimbursement`, body),
    getMemberReimbursementCaseFiles$: (caseId: string) =>
        getODataObject(`/odata/crm/myqueue/default.GetMemberReimbursementCaseFiles(caseId='${caseId}')`),
    getCaregiverBenefitDescriptionForMemberReimbursement$: (hcode: string, pbpId: string, planYear: string) =>
        getODataObject(
            `/odata/crm/myqueue/default.GetCaregiverBenefitDescriptionForMemberReimbursement?hcode=${hcode}&pbpId=${pbpId}&planYear=${planYear}`,
        ),
    getCaregiverBenefitAwardedDate$: (memberId: string) =>
        getODataObject(`/odata/crm/myqueue/default.GetCaregiverBenefitAwardedDate(memberId='${memberId}')`),

    getWorkflowTypes$: () => ajaxGetResponse(`/odata/crm/myqueue/default.GetWorkflowTypes`),
    getWorkflowStatuses$: () => ajaxGetResponse(`/odata/crm/myqueue/default.GetWorkflowStatuses`),
    getCrmInboundUsers$: () => ajaxGetResponse(`/odata/crm/myqueue/default.GetCrmInboundUsers`),

    getCrmBenefitInfo$: () => ajaxGetResponse(`/odata/crm/myqueue/default.GetCrmBenefitInfo`),
    updateCrmBenefitInfo$: (request: UpdateCrmBenefitInfoRequest) =>
        ajaxPost(`/odata/crm/myqueue/default.UpdateCrmBenefitInfo`, request),

    // workflow task
    createWorkflowTask$: (request: CreateWorkflowTaskRequest) =>
        ajaxPost(`/odata/crm/myqueue/default.CreateWorkflowTask`, request).pipe(
            map((ajaxResponse) => ajaxResponse.response),
        ),
    updateWorkflowTask$: (request: UpdateWorkflowTaskRequest) =>
        ajaxPost(`/odata/crm/myqueue/default.UpdateWorkflowTask`, request).pipe(
            map((ajaxResponse) => ajaxResponse.response),
        ),
    closeWorkflowTask$: (workflowTaskId: string) =>
        ajaxPost(`/odata/crm/myqueue/default.CloseWorkflowTask`, {
            workflowTaskId: workflowTaskId,
        }).pipe(map((ajaxResponse) => ajaxResponse.response)),

    addNoteToWorkflowTask$: (workflowTaskId: string, notes: string) =>
        ajaxPost(`/odata/crm/myqueue/default.AddNoteToWorkflowTask`, {
            workflowTaskId: workflowTaskId,
            notes: notes,
        }).pipe(map((ajaxResponse) => ajaxResponse.response)),

    getWorkflowTasksByWorkflowId$: (workflowId: string) =>
        ajaxGetResponse(
            `/odata/crm/myqueue/default.getworkflowtasks?$filter=workflowId eq '${workflowId}'&$skip=0&$top=50`,
        ),
    getWorkflowTasksByCaseId$: (caseId: string) =>
        ajaxGetResponse(
            `/odata/crm/myqueue/default.getworkflowtasks?$filter=caseId eq '${caseId}'&$orderby=CreatedDate desc&$skip=0&$top=50`,
        ),
    getWorkflowTaskById$: (id: string) =>
        ajaxGetResponse(`/odata/crm/myqueue/default.getworkflowtasks?$filter=id eq '${id}'&$skip=0&$top=50`).pipe(
            map((response) => response.value),
            filter((results) => results && results.length),
            map((results) => results[0]),
        ),

    getWorkflowTaskStatuses$: () => ajaxGetResponse(`odata/crm/myqueue/default.getWorkflowTaskStatuses`),
    getTaskTypes$: () => ajaxGetResponse(`odata/crm/myqueue/default.GetTaskTypes`),

    // service flow
    createServiceFlow$: (request: CreateServiceFlowRequest) => {
        return ajaxPost(`odata/crm/myqueue/default.CreateServiceFlow`, request).pipe(
            map((ajaxResponse) => ajaxResponse.response),
        );
    },
    updateServiceFlow$: (request: UpdateServiceFlowRequest) => {
        return ajaxPost(`/odata/crm/myqueue/default.UpdateServiceFlow`, request).pipe(
            map((ajaxResponse) => ajaxResponse.response),
        );
    },
    deleteServiceFlow$: (id: string) => {
        return ajaxPost(`/odata/crm/myqueue/default.DeleteServiceFlow`, {
            id: id,
        }).pipe(map((ajaxResponse) => ajaxResponse.response));
    },
    switchIsActiveServiceFlow$: (id: string) => {
        return ajaxPost(`/odata/crm/myqueue/default.SwitchIsActiveServiceFlow`, {
            id: id,
        }).pipe(map((ajaxResponse) => ajaxResponse.response));
    },

    getServiceFlow$: (id: string) => ajaxGetResponse(`/odata/crm/myqueue/default.getserviceflow(id='${id}')`),
    getServiceFlowForMember$: (id: string, memberId: string) =>
        ajaxGetResponse(`/odata/crm/myqueue/default.getServiceFlowForMember(id='${id}',memberId='${memberId}')`),
    getServiceFlows$: () =>
        ajaxGetResponse(`/odata/crm/myqueue/default.getserviceflows`).pipe(
            map((response: any) => response.value),
            map((serviceFlows: any) => serviceFlows.filter((flow) => flow.isActive && !flow.isDeleted)),
        ),
    getAllServiceFlows$: () => ajaxGetResponse(`/odata/crm/myqueue/default.getserviceflows`),
    getServiceFlowsForMember$: (memberId: string, effectiveDate: string): Observable<ServiceFlow[]> =>
        getODataArray(
            `/odata/crm/myqueue/default.GetServiceFlowsForMember?memberId=${memberId}&effectiveDate=${effectiveDate}`,
        ),

    // misc
    initialData$: () => ajaxGetResponse(`/odata/crm/myqueue/default.getinitialdata`),

    getInboundScript$: () => ajaxGetResponse('odata/crm/myqueue/default.getInboundScript'),

    getFeatureFlags$: () => ajaxGet(`api/crm/myqueue/GetFeatureFlags`),

    getAppSettings$: () => ajaxGetResponse('odata/crm/myqueue/default.getAppSettings'),

    getCompanyMaster$: () => ajaxGetResponse('odata/crm/myqueue/default.getCompanyMaster'), // TODO: remove

    getMsos$: () => ajaxGetResponse('odata/sessionorganizations?OrgType=MSO'),
    getAllMsos$: () => ajaxGetResponse('odata/crm/myqueue/default.GetAllMsos'),

    getQueues$: () => ajaxGetResponse(`odata/crm/myqueue/default.GetQueues`),

    getAdasLanguages$: () => ajaxGetResponse('odata/crm/myqueue/default.GetAdasLanguages'),
    getEzmLanguages$: () => ajaxGetResponse('odata/crm/myqueue/default.getezmemberlanguages'),

    getServiceFlowConfig$: () => getODataObject('odata/crm/myqueue/default.GetServiceFlowConfig'),
    updateServiceFlowConfig$: (body: ServiceFlowConfigRequest) =>
        ajaxPost('odata/crm/myqueue/default.UpdateServiceFlowConfig', body),

    getWorkflowTaskOptions$: () => ajaxGetResponse('odata/crm/myqueue/default.GetWorkflowTaskOptions'),
    updateCaseReassignmentStructureByQueue$: (body: UpdateCaseReassignmentStructureByQueueRequest) =>
        ajaxPost('odata/crm/myqueue/default.UpdateCaseReassignmentStructureByQueue', body),

    getCrmConfig$: () => ajaxGetResponse('odata/crm/myqueue/default.GetCrmConfig'),

    getRequestPlanMaterialOptions$: () => getODataObject('odata/crm/myqueue/default.getRequestPlanMaterialOptions'),
    updateRequestPlanMaterialOptions$: (body) =>
        ajaxPost('odata/crm/myqueue/default.updateRequestPlanMaterialOptions', body),

    updateTextReplaceOptions$: (body: UpdateTextReplaceOptions) =>
        ajaxPost('odata/crm/myqueue/default.UpdateTextReplaceOptions', body),

    getUserManagementQueues$: () => ajaxGetResponse(`/odata/usermanagementqueues`),

    getMemberPortalMessageCategories$: () =>
        getODataArray(`odata/crm/myqueue/default.getMemberPortalMessageCategories`),

    addQueueItem$: (queue: string) =>
        ajaxPost(`odata/crm/myqueue/default.AddQueueItem`, {
            queue: queue,
        }).pipe(map((ajaxResponse) => ajaxResponse.response)),

    pushQueues$: () =>
        ajaxPost(`odata/crm/myqueue/default.PushQueues`).pipe(map((ajaxResponse) => ajaxResponse.response)),

    getMyProductivityStats$: () => ajaxGetResponse('api/myqueue/my-stats'),

    getMyProductivityStatsTotalCases$: (): Observable<Case[]> => ajaxGetResponse('api/myqueue/my-stats/totalcases'),

    getMyProductivityStatsTotalClosedCases$: (): Observable<Case[]> =>
        ajaxGetResponse('api/myqueue/my-stats/totalclosedcases'),

    getMyProductivityStatsTotalOpenCases$: (): Observable<Case[]> =>
        ajaxGetResponse('api/myqueue/my-stats/totalopencases'),

    getMyProductivityStatsTotalOpenAssignedCases$: (): Observable<Case[]> =>
        ajaxGetResponse('api/myqueue/my-stats/totalopenassignedcases'),

    getMyProductivityStatsTotalOpenAssignedTasks$: (): Observable<WorkflowTask[]> =>
        ajaxGetResponse('api/myqueue/my-stats/totalopenassignedtasks'),

    getMyProductivityStatsCallCasesThisMonth$: (): Observable<Case[]> =>
        ajaxGetResponse('api/myqueue/my-stats/callcasesthismonth'),

    getMyProductivityStatsClosedCasesThisMonth$: (): Observable<Case[]> =>
        ajaxGetResponse('api/myqueue/my-stats/closedcasesthismonth'),

    getMyProductivityStatsOpenCasesThisMonth$: (): Observable<Case[]> =>
        ajaxGetResponse('api/myqueue/my-stats/opencasesthismonth'),

    getOpenCaseItemsForUser$: (userEmail: string) =>
        ajaxGetResponse(`/odata/crm/myqueue/default.GetOpenCaseItemsForUser(userEmail='${userEmail}')`),

    getMailRequestItemsByPlan$: (contractCode: string, planCode: string) =>
        ajaxPost<MailRequestItems>('/odata/crm/myqueue/default.GetMailRequestItemsByPlan', {
            contractCode,
            planCode,
        }).pipe(map((response) => response.response)),

    updateMailTypes$: (data) => ajaxPost('/odata/crm/myqueue/default.UpdateMailTypes', data),

    updateMailSubTypes$: (data) => ajaxPost('/odata/crm/myqueue/default.UpdateMailSubTypes', data),

    getMemberPcpHistory$: (memberId: string) =>
        ajaxGetResponse(`/odata/crm/myqueue/default.pcphistory(memberId='${memberId}')`),

    getAuthorizations$: (memberId: string) =>
        ajaxGetResponse(`/odata/crm/myqueue/default.getauthorizations(memberId='${memberId}')`),

    getMemberPbpHistory$: (memberId: string) =>
        ajaxGetResponse(`/odata/crm/myqueue/default.getpoliciescoverage(memberId='${memberId}')`),

    getNextBestActions$: (memberId: string) =>
        ajaxGetResponse(`/odata/crm/myqueue/default.GetNextBestActions(memberId='${memberId}')`),
    markNbaComplete$: (request: MarkNbaCompleteRequest) =>
        ajaxPost(`/odata/crm/myqueue/default.MarkNbaComplete`, request).pipe(
            map((ajaxResponse) => ajaxResponse.response),
        ),

    getActiveCounties$: () => ajaxGet(`/odata/crm/myqueue/default.getactivecounties`),
    getCountyByZipCode$: (zipCode: string) =>
        ajaxGetResponse(
            `/odata/crm/myqueue/default.getcountybyzipcode(getCountyByZipCodeRequest=@getCountyByZipCodeRequest)?@getCountyByZipCodeRequest={"@odata.type":"AHC.VNext.Infrastructure.CRM.RequestModels.Workflow.GetCountyByZipCodeRequest","ZipCode":"${zipCode}"}`,
        ),
    getStates$: () => ajaxGet(`/odata/crm/myqueue/default.getstates`),

    getAllHealthPlan$: () => ajaxGetResponse(`/odata/crm/myqueue/default.GetAllHealthPlan`),
    getHealthPlan$: (parameters: HealthPlanParameters) =>
        ajaxGetResponse(
            `/odata/crm/myqueue/default.GetHealthPlan?companyid=${parameters.companyid}&pbpid=${parameters.pbpid}&hcode=${parameters.hcode}&planyear=${parameters.planyear}&displayzipcodes=${parameters.displayzipcodes}`,
        ),

    getHealthPlanBenefitDetails$: (parameters: HealthPlanBenefitDetailsParameters) =>
        ajaxGetResponse(
            `/odata/crm/myqueue/default.GetHealthPlanBenefitDetails?companyId=${parameters.companyId}&hcode=${
                parameters.hcode
            }&pbpId=${parameters.pbpId || ''}&pbpType=${parameters.pbpType}&planYear=${
                parameters.planYear
            }&countyCode=${parameters.countyCode}&countyName=${parameters.countyName}&lineOfBusiness=${
                parameters.lineOfBusiness
            }&ipa=${parameters.ipa}&searchTerm=${parameters.searchTerm}&template=EOC`,
        ),

    getPlanDetailsByZipCode$: (zipCode: string, coverageYear: string) =>
        ajaxGetResponse(
            `/odata/crm/myqueue/default.GetPlanDetailsByZipCode?zipCode=${zipCode}&coverageYear=${coverageYear}`,
        ),

    getPoaEnums$: () => ajaxGetResponse(`/odata/crm/myqueue/default.GetPoaEnums`),

    getTalkdeskRecordingUrls$: (talkDeskInteractionId: string) =>
        ajaxGetResponse(
            `odata/crm/myqueue/default.GetTalkdeskRecordingUrls(talkDeskInteractionId='${talkDeskInteractionId}')`,
        ),
    searchProviders$: (parameters: ProviderSearchParameters, memberId: string | null) =>
        ajaxGetResponse(
            `/odata/crm/myqueue/default.searchproviders?pageIndex=${
                parameters.pageIndex ?? ''
            }&providerCategory=Primary Care Provider (PCP)&pbpId=${parameters.pbpId ?? ''}&healthplanid=${
                parameters.healthplanid ?? ''
            }&searchterm=${parameters.searchterm ?? ''}&fromAddress=${parameters.fromAddress ?? ''}&gender=${
                parameters.gender ?? ''
            }&language=${parameters.language ?? ''}&withinmiles=${parameters.withinmiles ?? ''}&acceptingNewPatients=${
                parameters.acceptingNewPatients ?? ''
            }&ipa=${parameters.ipa ?? ''}&sortby=qualityrating,${parameters.sortby ?? ''}&visitType=${
                parameters.visitType ?? ''
            }&planYear=${parameters.planYear ?? ''}&memberId=${memberId}`,
        ),

    addProvider$: (provider) => ajaxPost('/odata/crm/myqueue/default.AddProvider', provider),

    // supplemental benefits
    getSupplementalBenefitAllocationHistory$: (memberId: string, type = 'benefit') =>
        ajaxGetResponse(
            `/odata/crm/myqueue/default.GetSupplementalBenefitsAllocationHistoryByType(memberId='${memberId}',type='${type}')`,
        ),
    getSupplementalBenefitConfig$: (code: string, year: string, type: string) =>
        ajaxGetResponse(
            `/odata/crm/myqueue/default.GetSupplementalBenefitConfig(code='${code.replace(
                /\s|\\|\//gi,
                '',
            )}',year='${year}',type='${type}')`,
        ),
    upsertSupplementalBenefitConfig$: (body: UpsertSupplementalBenefitConfigRequest) =>
        ajaxPost('/odata/crm/myqueue/default.UpsertSupplementalBenefitConfig', body),
    getSupplementalBenefitsMasterList$: () =>
        ajaxGetResponse(`/odata/crm/myqueue/default.GetSupplementalBenefitsMaster`),
    getSupplementalBenefitsListByEligibility: (memberId: string) =>
        ajaxGet(`/odata/crm/myqueue/default.GetSupplementalBenefitsListByEligibility(memberId='${memberId}')`).pipe(
            map((ajaxResponse) => ajaxResponse.response),
        ),
    getSupplementalBenefitsDetailByEligibility$: (memberId: string) =>
        ajaxGet(`/odata/crm/myqueue/default.GetSupplementalBenefitsDetailByEligibility(memberId='${memberId}')`).pipe(
            map((ajaxResponse) => ajaxResponse.response),
        ),
    getSupplementalBenefitsDetail$: (memberId: string) =>
        ajaxGet(`/odata/crm/myqueue/default.GetSupplementalBenefitsDetail(memberId='${memberId}')`).pipe(
            map((ajaxResponse) => ajaxResponse.response),
        ),
    getSupplementalBenefitsDetailForBubbles$: (memberId: string) =>
        ajaxGet(`/odata/crm/myqueue/default.GetSupplementalBenefitsDetailForBubbles(memberId='${memberId}')`).pipe(
            map((ajaxResponse) => ajaxResponse.response),
        ),
    getStickyNotes$: (memberId: string) =>
        ajaxGet(`/odata/crm/myqueue/default.GetStickyNotes(memberId='${memberId}')`).pipe(
            map((ajaxResponse) => ajaxResponse?.response?.value ?? null),
        ),
    getCrmAdminStickyNotes$: () => ajaxGetResponse(`/odata/crm/myqueue/default.getCrmAdminStickyNotes`),
    getCrmAdminBulkStickyNotes$: () => ajaxGetResponse(`/odata/crm/myqueue/default.getCrmAdminBulkStickyNotes`),
    dismissStickyNote$: (noteId: string, active: boolean) =>
        ajaxPost(`/odata/crm/myqueue/default.dismissStickyNote`, { id: noteId, active: active }).pipe(
            map((ajaxResponse) => ajaxResponse.response),
        ),
    upsertStickyNote$: (memberId: string, note: string, active: boolean) =>
        ajaxPost(`/odata/crm/myqueue/default.upsertStickyNote`, { memberId, note, active }).pipe(
            map((ajaxResponse) => ajaxResponse.response),
        ),
    createStickyNote$: (request: StickyNoteRequest) => ajaxPost(`/odata/crm/myqueue/default.createStickyNote`, request),
    updateStickyNote$: (request: StickyNoteRequest) => ajaxPost(`/odata/crm/myqueue/default.updateStickyNote`, request),
    deleteStickyNote$: (id: string) => ajaxPost(`/odata/crm/myqueue/default.deleteStickyNote`, { id: id }),

    validateCountyZipCode$: (zipCode: string, countyCode: string) =>
        ajaxPost(`/odata/crm/myqueue/default.validateCountyZipCode`, {
            zipCode,
            countyCode,
        }).pipe(map((ajaxResponse) => ajaxResponse.response)),
    getMemberCallLogs$: (body: GetMemberCallLogsRequest) =>
        ajaxPost(`/odata/crm/myqueue/default.getMemberCallLogs`, body).pipe(map((ajaxResponse) => ajaxResponse)),
    getMemberTimeLine$: (memId: string) =>
        crmMemberTimelineSubject.asObservable().pipe(
            filter(({ memberId }) => memberId === memId),
            map(({ _, response }) => response),
        ),
    fetchMemberTimeLine$: (memberId: string) =>
        ajaxGet(`/odata/crm/myqueue/memberTimeline(memberId='${memberId}')`).subscribe({
            next: (response) => crmMemberTimelineSubject.next({ memberId, response }),
            error: () => crmMemberTimelineSubject.next({ memberId, error: "Member's health journey was not found" }),
        }),
    getUserQueueMappings$: () =>
        ajaxGet(`/odata/crm/myqueue/default.GetUserQueueMappings`).pipe(
            map((ajaxResponse) => ajaxResponse.response.value),
        ),
    getAuthorizationByAuthNo$: (authNo: string) =>
        ajaxGetResponse(`/odata/crm/myqueue/default.GetAuthorizationByAuthNo(authNo='${authNo}')`),
    getAuthorizationDetails$: (authNo: string) =>
        ajaxGetResponse(`/odata/crm/myqueue/default.GetAuthorizationDetails(authNo='${authNo}')`),
    getAuthAdmissions$: (authNo: string) =>
        ajaxGetResponse(`/odata/crm/myqueue/default.GetAuthAdmissionDetails(authNo='${authNo}')`),
    getMemberLookup$: (authNo: string) =>
        ajaxGetResponse(`/odata/crm/myqueue/default.MemberLookup(authNo='${authNo}')`),
    getAuthorizationLetter$: (authNo: string, letterId: number) =>
        ajaxGetType(`/api/crm/myqueue/authorizations/${authNo}/letters/${letterId}`, 'blob'),
    validateIncentiveName$: (incentiveName: string): Observable<BenefitValidationError> =>
        ajaxPost(`/odata/crm/myqueue/default.validateIncentiveName`, {
            incentiveName: incentiveName,
        }).pipe(map((ajaxResponse) => ajaxResponse.response)),

    //Mail Material Request
    processMailMaterialRequest$: (requestPlanMaterialForm, member, mailMaterialRequests, id) =>
        ajaxPost('api/crm/myqueue/ProcessMailMaterialRequest', {
            planMaterials: mailMaterialRequests,
            memberId: member.memberId,
            memberEffectiveDate: member.ezMemEffectiveDate,
            mailingAddressLine1: requestPlanMaterialForm.mailingAddressLine1,
            mailingAddressLine2: requestPlanMaterialForm.mailingAddressLine2,
            mailingAddressCity: requestPlanMaterialForm.mailingAddressCity,
            mailingAddressState: requestPlanMaterialForm.mailingAddressState,
            mailingAddressZip: requestPlanMaterialForm.mailingAddressZip,
            email: requestPlanMaterialForm.email,
            id: id,
        }),

    //Email & Mail Status
    GetEmailStatus$: () => ajaxGet('api/crm/myqueue/GetEmailStatus/GetEmailStatus'),

    GetMailStatus$: () => ajaxGet('api/crm/myqueue/GetMailStatus/GetMailStatus'),

    getStarMeasureMed$: (memberId: string) =>
        ajaxGetResponse(`/odata/crm/myqueue/default.GetStarMeasureMedications(memberId='${memberId}')`),

    getMemberPortalEvents$: (memberId: string, memberEmail: string) =>
        ajaxGetResponse(
            `/odata/crm/myqueue/default.GetMemberPortalEventsById(memberId='${memberId}',memberEmail='${memberEmail}')`,
        ),
    getMemberPortalAccountEvents$: (memberId: string) =>
        ajaxGetResponse(`/odata/crm/myqueue/default.GetMemberPortalAccountEvents(memberId='${memberId}')`),

    //Claim
    getClaimByNo$: (claimNo: string, companyId: string) =>
        ajaxGetResponse(`/odata/crm/myqueue/default.GetClaimByNo(claimNo='${claimNo}',companyId='${companyId}')`),
    getMemberNotes$: (memberId: string) =>
        ajaxGetResponse(`/odata/crm/myqueue/default.GetMemberContacts(memberId='${memberId}')`),
    getMemberWallets$: (memberId: string) =>
        ajaxGetResponse<GetMemberWalletsResponse>(
            `/odata/crm/myqueue/default.GetMemberWallets(memberId='${memberId}')`,
        ),
    getHraTagInfo$: (memberId: string) =>
        getODataObject<MemberTag>(`/odata/crm/myqueue/default.GetHraTagInfo(memberId='${memberId}')`),
    getMemberOnePassCode: (memberId: string): Observable<OnePassCode> =>
        getODataObject<OnePassCode>(`/odata/crm/myqueue/default.GetMemberOnePassCode(memberId='${memberId}')`),

    //worklist
    getWorklistStats$: (userEmail: string) =>
        getODataObject(`/odata/crm/myqueue/default.GetWorklistStats(userEmail='${userEmail}')`),
    getWorklistUrgentCases$: (userEmail: string) =>
        getODataArray(`/odata/crm/myqueue/default.GetWorklistUrgentCases(userEmail='${userEmail}')`),
    getWorklistRecentlyClosedTasks$: (userEmail: string) =>
        getODataArray(`/odata/crm/myqueue/default.GetWorklistRecentlyClosedTasks(userEmail='${userEmail}')`),
    getWorklistRecentlyClosedRejectedTasks$: (userEmail: string) =>
        getODataArray(`/odata/crm/myqueue/default.GetWorklistRecentlyClosedRejectedTasks(userEmail='${userEmail}')`),
    getWorklistCasesCandidatesToClose$: (userEmail: string) =>
        getODataArray(`/odata/crm/myqueue/default.GetWorklistCasesCandidatesToClose(userEmail='${userEmail}')`),
    getWorklistWorkflowsExceedingSlaNoOpenTasks$: (userEmail: string) =>
        getODataArray(
            `/odata/crm/myqueue/default.GetWorklistWorkflowsExceedingSlaNoOpenTasks(userEmail='${userEmail}')`,
        ),
    getWorklistCasesLastUpdatedByDifferentId$: (userEmail: string) =>
        getODataArray(`/odata/crm/myqueue/default.GetWorklistCasesLastUpdatedByDifferentId(userEmail='${userEmail}')`),
    getWorklistOpenTasksExceedingSla$: (userEmail: string) =>
        getODataArray(`/odata/crm/myqueue/default.GetWorklistOpenTasksExceedingSla(userEmail='${userEmail}')`),
    getWorklistWorkflowsExceedingSlaHasOpenTasks$: (userEmail: string) =>
        getODataArray(
            `/odata/crm/myqueue/default.GetWorklistWorkflowsExceedingSlaHasOpenTasks(userEmail='${userEmail}')`,
        ),
    getWorklistCasesWithinSla$: (userEmail: string) =>
        getODataArray(`/odata/crm/myqueue/default.GetWorklistCasesWithinSla(userEmail='${userEmail}')`),
    getWorklistWorkflowsWithinSla$: (userEmail: string) =>
        getODataArray(`/odata/crm/myqueue/default.GetWorklistWorkflowsWithinSla(userEmail='${userEmail}')`),

    getOdataArrayAsync: async <tResult>(endpoint: string, setter: (value: tResult[]) => void, onError: () => void) => {
        const headers = {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${Auth.getToken()}`,
                'Content-Type': 'application/json',
            },
        };
        return await fetch(`${endpoint}`, headers)
            .then((response) => response?.json())
            .then((response) => {
                if (!response) {
                    onError();
                    return null;
                }
                const data = response as ODataArrayResponse<tResult>;
                return data.value || null;
            })
            .then((result) => (result ? setter(result) : onError()))
            .catch((_) => onError());
    },
    getPromiseAsync: async <tResult>(endpoint: string, onError?: (reason?: any) => void): Promise<tResult | null> => {
        const headers = {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${Auth.getToken()}`,
                'Content-Type': 'application/json',
            },
        };
        return await fetch(`${endpoint}`, headers)
            .then((response: Response) => (response.status === 200 ? response?.json() : null))
            .then((response) => {
                if (!response) {
                    onError && onError();
                    return null;
                }
                return response;
            })
            .catch((_) => onError && onError(_));
    },
    postAsync: async <tResult>(
        endpoint: string,
        params: any | null,
        handler: (response: tResult) => void,
        onError: ((e: Error) => void) | (() => void),
    ) => {
        const promise = fetch(`${endpoint}`, getFetchHeader('POST', params));
        await handleFetchResponse(promise, handler, onError);
    },
    patchAsync: async <tResult>(
        endpoint: string,
        params: any | null,
        handler: (response: tResult) => void,
        onError: ((e: Error) => void) | (() => void),
    ) => {
        const promise = fetch(`${endpoint}`, getFetchHeader('PATCH', params));
        await handleFetchResponse(promise, handler, onError);
    },
    deleteAsync: async <tResult>(
        endpoint: string,
        params: any | null,
        handler: (response: tResult) => void,
        onError: ((e: Error) => void) | (() => void),
    ) => {
        const promise = fetch(`${endpoint}`, getFetchHeader('DELETE', params));
        await handleFetchResponse(promise, handler, onError);
    },
    postPromiseAsync: async <tResult>(endpoint: string, params: any, onError: () => void): Promise<tResult | null> =>
        await fetch(`${endpoint}`, getFetchHeader('POST', params))
            .then(async (response) => {
                if (response.status === 204) {
                    return null;
                } else if (response.ok) {
                    return response.json();
                } else if (response.status === 400) {
                    const content = await response.json();
                    throw new Error(content.error);
                }
                throw new Error(response.status?.toString());
            })
            .catch((_) => onError()),
};
const getFetchHeader = (method: string, body: any) => ({
    method: method,
    headers: {
        Authorization: `Bearer ${Auth.getToken()}`,
        'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
});
const handleFetchResponse = async <tResult>(
    result: Promise<Response>,
    handler: (((response: tResult) => void) | (() => void)),
    onError: (error: Error) => void,
) =>
    await result
        .then(async (response) => {
            if (response.status === 204) {
                return undefined;
            } else if (response.ok) {
                return response.json();
            } else if (response.status === 400) {
                const content = await response.json();
                throw new Error(content.error);
            }
            throw new Error(response.status.toString());
        })
        .then((data) => {
            handler(data);
        })
        .catch(onError);

const getStringProperty = (property, replaceNonWords?) => {
    if (property === null || property?.trim() === '') {
        return null;
    } else if (replaceNonWords) {
        return `'${property.toUpperCase().replace(/\W/g, '')}'`;
    } else {
        return `'${property.toUpperCase()}'`;
    }
};
const getArrayToStringProperty = (property) => {
    if (property === null || property === undefined) {
        return null;
    } else {
        const allItems = property
            .map((p) => `'${p}'`)
            .join(',')
            .toUpperCase();
        return `[${allItems}]`;
    }
};
const getDateProperty = (property) => {
    if (property === null || property === undefined) {
        return null;
    } else {
        return property.toJSON();
    }
};

export { myQueueService, getDateProperty };
